import React, { useState } from 'react';
import { Lock, LogIn, KeyRound, Mail } from 'lucide-react';
import { useAuth, demanderReinitialisation } from '../context/AuthContext';
import ReinitialisationMotDePasse from '../pages/ReinitialisationMotDePasse';
import './LoginPage.css';
import '../pages/ReinitialisationMotDePasse.css';

/**
 * Écran de connexion affiché par le gate conditionnel quand l'auth est requise
 * et que l'utilisateur n'est pas authentifié.
 *
 * S'adapte au mode renvoyé par /api/auth/config :
 * - 'jwt'    : formulaire identifiant / mot de passe → POST /api/auth/login ;
 * - 'apikey' : champ clé API unique.
 *
 * Le lien de réinitialisation envoyé par Supabase ramène sur `/reinitialisation`
 * une personne par définition NON connectée : c'est donc cet écran, rendu par le
 * gate à la place de l'application, qui affiche la page de réinitialisation.
 */
export default function LoginPage() {
  if (typeof window !== 'undefined' && window.location.pathname === '/reinitialisation') {
    return <ReinitialisationMotDePasse />;
  }
  return <FormulaireConnexion />;
}

/**
 * « Mot de passe oublié ? » : demande du lien par e-mail. Le message affiché est
 * le même que l'adresse corresponde à un compte ou non.
 */
function MotDePasseOublie({ onRetour }) {
  const [email, setEmail] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState(null);
  const [erreur, setErreur] = useState(null);

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur(null);
    setMessage(null);
    const adresse = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adresse)) {
      setErreur('Saisissez une adresse e-mail valide.');
      return;
    }
    setEnvoi(true);
    try {
      const resultat = await demanderReinitialisation(adresse);
      if (resultat === 'trop-de-tentatives') {
        setErreur('Trop de tentatives. Réessayez dans une minute.');
      } else {
        setMessage(
          'Si un compte correspond à cette adresse, un lien de réinitialisation vient d’être envoyé. Pensez à vérifier vos courriels indésirables.'
        );
      }
    } catch (err) {
      setErreur(err.message || 'Serveur injoignable');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form className="login-form" onSubmit={soumettre} noValidate>
      <div className="login-field">
        <label htmlFor="oubli-email">Adresse e-mail du compte</label>
        <input
          id="oubli-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      {erreur && (
        <p className="login-error" role="alert">
          {erreur}
        </p>
      )}
      {message && (
        <p className="login-info" role="status">
          {message}
        </p>
      )}

      <button type="submit" className="login-submit" disabled={envoi}>
        <Mail size={20} aria-hidden />
        <span>{envoi ? 'Envoi…' : 'Recevoir un lien'}</span>
      </button>
      <button type="button" className="login-link" onClick={onRetour}>
        Retour à la connexion
      </button>
    </form>
  );
}

function FormulaireConnexion() {
  const { mode, dataLocality, login, loginWithApiKey } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [oubli, setOubli] = useState(false);

  const isApiKeyMode = mode === 'apikey';
  // La récupération par e-mail n'existe qu'avec Supabase : en mode local les
  // comptes vivent sur ce serveur et n'ont pas d'adresse vérifiée.
  const recuperationPossible = !isApiKeyMode && dataLocality === 'cloud';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      if (isApiKeyMode) {
        await loginWithApiKey(apiKey.trim());
      } else {
        await login(username.trim(), password);
      }
    } catch (err) {
      setFormError(err.message || 'Échec de la connexion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <main className="login-card" role="main">
        <div className="login-brand">
          <div className="login-brand-icon" aria-hidden>
            <Lock size={24} />
          </div>
          <div>
            <h1 className="login-title">Biblio-Tech RAG</h1>
            <p className="login-subtitle">
              {oubli ? 'Mot de passe oublié' : 'Connexion requise'}
            </p>
          </div>
        </div>

        {oubli ? (
          <MotDePasseOublie onRetour={() => setOubli(false)} />
        ) : (
        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {isApiKeyMode ? (
            <div className="login-field">
              <label htmlFor="login-api-key">Clé API</label>
              <input
                id="login-api-key"
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Votre clé API"
                required
              />
            </div>
          ) : (
            <>
              <div className="login-field">
                <label htmlFor="login-username">Identifiant</label>
                <input
                  id="login-username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Nom d'utilisateur"
                  required
                  minLength={3}
                />
              </div>
              <div className="login-field">
                <label htmlFor="login-password">Mot de passe</label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            </>
          )}

          {formError && (
            <p className="login-error" role="alert">
              {formError}
            </p>
          )}

          <button type="submit" className="login-submit" disabled={submitting}>
            {isApiKeyMode ? <KeyRound size={20} aria-hidden /> : <LogIn size={20} aria-hidden />}
            <span>{submitting ? 'Connexion…' : 'Se connecter'}</span>
          </button>

          {recuperationPossible && (
            <button type="button" className="login-link" onClick={() => setOubli(true)}>
              Mot de passe oublié ?
            </button>
          )}
        </form>
        )}

        <p className="login-hint">
          {dataLocality === 'cloud'
            ? 'Authentification gérée par Supabase — votre mot de passe ne transite jamais par ce serveur.'
            : 'Application local-first — vos identifiants restent sur votre serveur.'}
        </p>
      </main>
    </div>
  );
}
